import type { CohortNames, IAbTestingState } from '@bridebook/toolbox/src/ab-testing/lib/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export const initialState: IAbTestingState = {
  abTests: {},
  abTestsLoaded: false,
  abTestsLifecycleState: {},
};

export const abTestingSlice = createSlice({
  name: 'abTesting',
  initialState,
  reducers: {
    setAbTestVariant(
      state: IAbTestingState,
      action: PayloadAction<{ id: string; variant: CohortNames }>,
    ) {
      const { id, variant } = action.payload;
      state.abTests[id] = variant;
    },
    /**
     * Overrides a test variant from the devtools, covering both the legacy variant
     * map and the lifecycle state the current hooks read. Marking the test as
     * loaded also stops the activation hook from resolving over the override.
     */
    forceAbTestVariant(
      state: IAbTestingState,
      action: PayloadAction<{ id: string; variant: CohortNames }>,
    ) {
      const { id, variant } = action.payload;
      state.abTests[id] = variant;
      state.abTestsLifecycleState[id] = { status: 'loaded', variant };
    },
    setAbTestsLoaded(state: IAbTestingState, action: PayloadAction<boolean>) {
      state.abTestsLoaded = action.payload;
    },
    markAbTestAsLoading(state: IAbTestingState, action: PayloadAction<string>) {
      const testId = action.payload;
      const current = state.abTestsLifecycleState[testId];
      if (!current || current.status === 'skipped') {
        state.abTestsLifecycleState[testId] = { status: 'loading', variant: null };
      }
    },
    markAbTestAsLoaded(
      state: IAbTestingState,
      action: PayloadAction<{ id: string; variant: CohortNames }>,
    ) {
      const { id, variant } = action.payload;
      state.abTestsLifecycleState[id] = { status: 'loaded', variant };
    },
    markAbTestAsError(state: IAbTestingState, action: PayloadAction<string>) {
      const testId = action.payload;
      state.abTestsLifecycleState[testId] = { status: 'error', variant: 'control' };
    },
    markAbTestAsSkipped(state: IAbTestingState, action: PayloadAction<string>) {
      const testId = action.payload;
      state.abTestsLifecycleState[testId] = { status: 'skipped', variant: 'control' };
    },
  },
});

export const {
  setAbTestVariant,
  forceAbTestVariant,
  setAbTestsLoaded,
  markAbTestAsLoading,
  markAbTestAsLoaded,
  markAbTestAsError,
  markAbTestAsSkipped,
} = abTestingSlice.actions;
export default abTestingSlice.reducer;
